<template>
  <v-card v-if="agent || (!agent && responseLocked)" class="pa-4 mt-4">
    <v-alert
      v-if="agent"
      dense
      text
      type="info"
    >
      Esitäytä vastaus ja lähetä allekirjoitettavaksi!
    </v-alert>
    <h2 class="text-body-1 mt-2 mb-2">
      Vastauksenne {{ referring }} annettuun tarjoukseen
    </h2>
    <v-radio-group
      v-model="responseOffer.choice"
      class="mt-2"
    >
      <v-radio
        v-for="(n, index) in choices"
        :key="index"
        :label="n.text"
        :value="n.value"
        :disabled="responseLocked || someHaveSigned"
      />
    </v-radio-group>
    <v-slide-y-transition>
      <v-textarea
        v-if="responseOffer.choice === 1"
        v-model="responseOffer.exceptionText"
        :disabled="responseLocked || someHaveSigned"
        outlined
        label="Vaaditut muutokset tarjoukseen"
        maxlength="400"
        counter="400"
      />
    </v-slide-y-transition>
    <v-container
      v-if="responseOffer.choice === 1"
      class="pa-0"
      grid-list-md
    >
      <v-row
        wrap
        class="mt-3"
      >
        <v-col
          cols="12"
          sm="6"
        >
          <v-menu
            v-model="datePickerMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :disabled="responseLocked || someHaveSigned"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ on }">
              <v-text-field
                v-model="offerValidUntilDateFormatted"
                :disabled="responseLocked || someHaveSigned"
                outlined
                label="Tarjous voimassa asti"
                append-icon="event"
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="responseOffer.offerValidUntilDate"
              :disabled="responseLocked || someHaveSigned"
              locale="fi-FI"
              first-day-of-week="1"
              @input="datePickerMenu = false;"
            />
          </v-menu>
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-text-field
            v-model="responseOffer.offerValidUntilTime"
            v-mask="'##:##'"
            :disabled="responseLocked || someHaveSigned"
            :rules="timeRule"
            outlined
            label="Voimassa kellonaikaan asti"
            append-icon="access_time"
          />
        </v-col>
      </v-row>

      <v-alert
        v-if="agent && showWarning"
        dense
        text
        type="error"
      >
        Huom! Vastausta ei voi tällä hetkellä allekirjoittaa.
        Muistathan hyväksyä tekemäsi muutokset.
      </v-alert>
    </v-container>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        v-if="!responseLocked && !someHaveSigned"
        color="primary"
        class="px-3"
        large
        depressed
        @click="saveResponse(responseOffer)"
      >
        Hyväksy
      </v-btn>
      <v-btn
        v-if="responseLocked && !someHaveSigned && agent"
        color="primary"
        text
        @click="editResponse()"
      >
        Muokkaa
      </v-btn>
    </v-card-actions>
    <nets-signature
      v-if="!agent && (responseLocked || someHaveSigned)"
      :form="form"
      :signatures="signatures"
      :signers="signers"
      :company-id="companyId"
      :create-date="createDate"
      :response-index="responses.length + 1"
    />
  </v-card>
</template>
<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { mask } from 'vue-the-mask';
import { apiGet } from '../api';

export default {
  name: 'ResponseOffer',
  components: {
    'nets-signature': () => import('@/components/NetsSignature'),
  },
  directives: {
    mask,
  },
  props: {
    sellers: {
      type: Array,
      default: () => [],
    },
    referring: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      default: () => {},
    },
    companyId: {
      type: String,
      default: '',
    },
    createDate: {
      type: String,
      default: '',
    },
    responses: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    showWarning: false,
    signatureDialog: false,
    datePickerMenu: false,
    responseLocked: false,
    signatures: [],
    timeRule: [
      v => v.length >= 5 || 'Syötä muodossa HH:MM',
      v => (parseInt(v.substring(0, 2), 10) < 24) || 'Virheellinen aika',
      v => (parseInt(v.substring(3, 5), 10) < 60) || 'Virheellinen aika',
    ],
    choices: [
      {
        text: 'Hyväksymme tarjouksen',
        value: 0,
      },
      {
        text: 'Hyväksymme tarjouksen seuraavin muutoksin',
        value: 1,
      },
      {
        text: 'Hylkäämme tarjouksen',
        value: 2,
      },
    ],
    responseOffer: {
      choice: 0,
      exceptionText: '',
      responseDate: moment(new Date()).format('DD.MM.YYYY H:mm'),
      signingDate: new Date().toISOString().substr(0, 10),
      offerValidUntilDate: new Date().toISOString().substr(0, 10),
      offerValidUntilTime: '12:00',
      signerRole: 'seller',
    },
    responseId: null,
  }),
  computed: {
    ...mapGetters({
      signerRole: 'signerRole',
      agent: 'agent',
    }),
    signers() {
      if (this.signerRole === 'seller') {
        return this.sellers;
      }
      return this.form.offerers;
    },
    signingDateFormatted() {
      return this.formatDate(this.responseOffer.signingDate);
    },
    offerValidUntilDateFormatted() {
      return this.formatDate(this.responseOffer.offerValidUntilDate);
    },
    someHaveSigned() {
      return this.signatures.length > 0;
    },
    signersHaveSigned() {
      if (this.signatures.length > 0) {
        return this.signatures.length === this.signers.length;
      }
      return false;
    },
  },
  created() {
    this.$bus.$on('SIGNATURE__CLOSE', () => {
      this.signatureDialog = false;
    });
    this.getSignatures();
    this.$bus.$on('loadLatestSignatures', async () => {
      await this.getSignatures();
      if (this.signerRole === 'seller') {
        if (this.signatures.length > 0 && this.signatures.length === this.sellers.length) {
          await apiGet(`properties/confirmresponse/${this.$route.params.offerId}/${this.responseId}`);
        }
      } else if (
        this.signatures.length > 0 && this.signatures.length === this.form.offerers.length
      ) {
        await apiGet(`properties/confirmresponse/${this.$route.params.offerId}/${this.responseId}`);
      }
    });
    this.getUnsignedResponse();
    this.responseOffer.signerRole = this.signerRole;
  },
  methods: {
    ...mapActions({
      addResponse: 'addResponse',
    }),
    async getSignatures() {
      const responseidx = this.responses.length + 1;
      const response = await apiGet(`nets/signatures/${this.$route.params.id}/${this.$route.params.offerId}/${responseidx}`);
      if (response.result.length) {
        this.signatures = response.result;
      }
    },
    async saveResponse(responseOffer) {
      await this.addResponse(responseOffer);
      this.responseLocked = true;
      this.showWarning = false;
      this.$bus.$emit('responseSendable');
    },
    async editResponse() {
      this.responseLocked = false;
      this.showWarning = true;
      const response = await apiGet(`properties/unsignedresponse/${this.$route.params.offerId}`);
      await apiGet(`properties/deleteresponse/${response.result.id}`);
      this.$bus.$emit('responseNotSendable');
    },
    async getUnsignedResponse() {
      const response = await apiGet(`properties/unsignedresponse/${this.$route.params.offerId}`);
      if (response.result && response.result.fields) {
        const responseDetails = JSON.parse(response.result.fields);
        this.responseId = response.result.id;
        if (responseDetails !== null) {
          this.responseOffer = responseDetails;
          this.responseLocked = true;
          this.$bus.$emit('responseSendable');
        }
      }
    },
    formatDate(date) {
      return moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY');
    },
  },
};
</script>
<style lang="scss">
.v-text-field .v-label {
  background: #fff;
  padding-right: 4px;
}
</style>
